/**
 * FERP Portal - AWS STS Integration
 *
 * Exchanges OIDC tokens for temporary AWS credentials via STS AssumeRoleWithWebIdentity.
 * Enables IAM policy enforcement for DynamoDB tenant isolation.
 *
 * Adapted from: forms-host/src/utils/aws-sts-integration.ts
 *
 * Three-Layer Authentication Flow:
 * 1. Authentik OIDC (WebAuthn/TOTP) → ID Token
 * 2. AWS STS AssumeRoleWithWebIdentity → Temporary Credentials
 * 3. IAM Policy Enforcement → DynamoDB LeadingKeys Isolation
 */

import { AssumeRoleWithWebIdentityCommand, STSClient, type Credentials } from '@aws-sdk/client-sts';

// User types that determine which AWS role to assume
export enum UserType {
  HEALTHCARE = 'healthcare',
  BUSINESS = 'business',
  ADMIN = 'admin',
}

// AWS STS Configuration per user type
interface STSConfig {
  region: string;
  roleArn: string;
  durationSeconds: number;
}

import { getEnv, getGlobalIdToken, isDevModeEnabled, shouldSkipAwsSts } from '@/utils/env';

// Temporary AWS Credentials
export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: Date;
  region: string;
}

// Role mappings per user type (populated from environment)
const getRoleMappings = (): Record<UserType, STSConfig> => {
  const env = getEnv();
  const region = env.AWS_REGION || 'us-east-2';

  return {
    [UserType.HEALTHCARE]: {
      region,
      roleArn: env.AWS_HEALTHCARE_ROLE_ARN || '',
      durationSeconds: 3600, // 1 hour for life-critical operations
    },
    [UserType.BUSINESS]: {
      region,
      roleArn: env.AWS_BUSINESS_ROLE_ARN || '',
      durationSeconds: 7200, // 2 hours for business operations
    },
    [UserType.ADMIN]: {
      region,
      roleArn: env.AWS_ADMIN_ROLE_ARN || '',
      durationSeconds: 14400, // 4 hours for administrative tasks
    },
  };
};

// STS Client singleton
let stsClient: STSClient | null = null;

// Module-scoped credential cache — never in sessionStorage (XSS risk)
let cachedCredentials: AwsCredentials | null = null;

function getSTSClient(): STSClient {
  if (!stsClient) {
    const env = getEnv();
    const region = env.AWS_REGION || 'us-east-2';

    if (!region) {
      throw new Error('AWS region not configured. Set AWS_REGION in environment.');
    }

    stsClient = new STSClient({ region });
  }
  return stsClient;
}

/**
 * Parse JWT token payload
 */
function parseJWT(token: string | null): any {
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64 = (parts[1] || '')
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const padded = base64.padEnd(
      base64.length + (4 - base64.length % 4) % 4,
      '='
    );

    return JSON.parse(atob(padded));
  } catch (error) {
    console.warn('Failed to parse JWT:', error);
    return null;
  }
}

/**
 * Get ID token from storage
 */
function getIdToken(): string | undefined {
  try {
    // Check global first (memory backup)
    const fromGlobal = getGlobalIdToken();
    if (fromGlobal) return fromGlobal;

    // Then check sessionStorage
    const fromSession = sessionStorage.getItem('auth:id_token') || undefined;
    if (fromSession) return fromSession;
  } catch {
    // Storage access failed
  }
  return undefined;
}

/**
 * Validate JWT token for AWS STS requirements
 */
function validateTokenForSts(token: string): { valid: boolean; payload: any; issues: string[] } {
  const payload = parseJWT(token);
  const issues: string[] = [];

  if (!payload) {
    issues.push('Invalid or malformed JWT token');
    return { valid: false, payload: null, issues };
  }

  // Check required claims for AssumeRoleWithWebIdentity
  if (!payload.iss) issues.push('Missing "iss" (issuer) claim');
  if (!payload.aud) issues.push('Missing "aud" (audience) claim');
  if (!payload.sub) issues.push('Missing "sub" (subject) claim');
  if (!payload.exp) issues.push('Missing "exp" (expiration) claim');

  // Check token expiration
  if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
    issues.push('Token is expired');
  }

  // Check tenant claim (required for tenant isolation)
  const tenant =
    payload.tenant ||
    payload['custom:tenant_id'] ||
    payload?.ak_proxy?.user_attributes?.tenant;

  if (!tenant) {
    issues.push('Missing tenant claim for tenant isolation');
  }

  return {
    valid: issues.length === 0,
    payload,
    issues,
  };
}

/**
 * Exchange OIDC ID token for AWS temporary credentials
 */
export async function exchangeTokenForAwsCredentials(
  idToken: string,
  userType: UserType
): Promise<AwsCredentials> {
  // Skip in development mode
  if (isDevModeEnabled() && shouldSkipAwsSts()) {
    console.log('🔧 Dev mode: Skipping AWS STS, returning mock credentials');
    return {
      accessKeyId: 'LOCAL_DEV_ACCESS_KEY',
      secretAccessKey: 'LOCAL_DEV_SECRET_KEY',
      sessionToken: 'LOCAL_DEV_SESSION_TOKEN',
      expiration: new Date(Date.now() + 3600 * 1000),
      region: 'us-east-2',
    };
  }

  const roleMappings = getRoleMappings();
  const stsConfig = roleMappings[userType];

  if (!stsConfig?.roleArn) {
    throw new Error(`No AWS role configured for user type: ${userType}`);
  }

  // Validate token
  const validation = validateTokenForSts(idToken);
  if (!validation.valid) {
    throw new Error(`Token validation failed: ${validation.issues.join(', ')}`);
  }

  // Extract tenant for session name
  const tenant =
    validation.payload.tenant ||
    validation.payload['custom:tenant_id'] ||
    validation.payload?.ak_proxy?.user_attributes?.tenant ||
    'unknown';

  const sessionName = `ferp-${userType}-${tenant}-${Date.now()}`
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .slice(0, 64);

  if (isDevModeEnabled()) {
    console.log('🔄 Exchanging OIDC token for AWS credentials:', {
      userType,
      region: stsConfig.region,
    });
  }

  try {
    const stsClient = getSTSClient();

    const command = new AssumeRoleWithWebIdentityCommand({
      RoleArn: stsConfig.roleArn,
      WebIdentityToken: idToken,
      RoleSessionName: sessionName,
      DurationSeconds: stsConfig.durationSeconds,
    });

    const response = await stsClient.send(command);

    if (!response.Credentials) {
      throw new Error('No credentials returned from STS');
    }

    const credentials: Credentials = response.Credentials;

    if (!credentials.AccessKeyId || !credentials.SecretAccessKey || !credentials.SessionToken) {
      throw new Error('Incomplete credentials returned from STS');
    }

    const awsCredentials: AwsCredentials = {
      accessKeyId: credentials.AccessKeyId,
      secretAccessKey: credentials.SecretAccessKey,
      sessionToken: credentials.SessionToken,
      expiration: credentials.Expiration || new Date(Date.now() + stsConfig.durationSeconds * 1000),
      region: stsConfig.region,
    };

    // Cache in module scope only — never sessionStorage (XSS-exfiltrable)
    cachedCredentials = awsCredentials;

    return awsCredentials;
  } catch (error) {
    console.error('❌ AWS credential exchange failed:', error);

    if (error instanceof Error) {
      if (error.message.includes('AccessDenied')) {
        throw new Error('Access denied: Check IAM role trust policy');
      }
      if (error.message.includes('InvalidIdentityToken')) {
        throw new Error('Invalid identity token: Check OIDC provider configuration');
      }
    }

    throw new Error(`AWS credential exchange failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Check if cached credentials are valid
 */
export function areCredentialsValid(credentials: AwsCredentials): boolean {
  return new Date(credentials.expiration) > new Date();
}

/**
 * Get cached AWS credentials if valid (module-scoped, not sessionStorage)
 */
export function getCachedAwsCredentials(): AwsCredentials | null {
  if (cachedCredentials && areCredentialsValid(cachedCredentials)) {
    return cachedCredentials;
  }
  cachedCredentials = null;
  return null;
}

/**
 * Refresh credentials if expiring soon (within 5 minutes)
 */
export async function refreshCredentialsIfNeeded(
  idToken: string,
  userType: UserType,
  currentCredentials: AwsCredentials
): Promise<AwsCredentials> {
  const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);

  if (new Date(currentCredentials.expiration) < fiveMinutesFromNow) {
    if (isDevModeEnabled()) console.log('🔄 Refreshing AWS credentials (expiring soon)');
    return await exchangeTokenForAwsCredentials(idToken, userType);
  }

  return currentCredentials;
}

/**
 * Clear cached credentials (on logout)
 */
export function clearAwsCredentials(): void {
  cachedCredentials = null;
}

/**
 * Create AWS SDK clients with temporary credentials
 */
export function createAwsSdkClientConfig(credentials: AwsCredentials) {
  return {
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    region: credentials.region,
  };
}

export default {
  exchangeTokenForAwsCredentials,
  areCredentialsValid,
  getCachedAwsCredentials,
  refreshCredentialsIfNeeded,
  clearAwsCredentials,
  createAwsSdkClientConfig,
  UserType,
};
